import React from 'react';

import UploadFilesCtrl from '@webapp/controllers/upload/upload-files';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import AppState from '@webapp/store/state';
import { AssetFile } from '@common/types/data/asset-file';
import { Translate } from 'react-localize-redux';

import UnselectedIcon from '@components/basics/icons/unselected';
import Typography from '@components/basics/typography';
import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';
import ErrorMessage from '@components/basics/error-message';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import MotionOpacity from '@components/motion/motion-opacity';
import { AnimatePresence } from 'framer-motion';
import { InputField } from '@webapp/components/form/input';
import Flex from '@components/layout/flex';

type TermsType = 'produced' | 'purchased' | '';

interface CoverCopyrightMaterialCtrlProps {
  className?: string;
  trackId: string;
}
interface StateProps {
  uploadedFilename?: string;
  onChange: (
    value: boolean,
    trackId: string
  ) => UpdateTrackField<'ownProduced'>;
  onLinkAdded: (
    value: string,
    trackId: string
  ) => UpdateTrackField<'freeBeatLink'>;
  updateFile: (
    file: AssetFile,
    trackId: string
  ) => UpdateTrackField<'copyrightDocuments'>;
  ownProduced: TermsType;
  freeBeatLink?: string;
}
const mapStateToProps = (
  state: AppState,
  props: CoverCopyrightMaterialCtrlProps
) => {
  const ownProduced =
    state.tracks.tracks[props.trackId].ownProduced === undefined
      ? ''
      : state.tracks.tracks[props.trackId].ownProduced === true
      ? 'produced'
      : 'purchased';
  let uploadedFilename = '';
  const track = state.tracks.tracks[props.trackId];
  if (track.copyrightDocuments) {
    uploadedFilename = track?.copyrightDocuments[0]?.name ?? '';
  }

  return {
    ownProduced: ownProduced,
    uploadedFilename: uploadedFilename,
    freeBeatLink: state.tracks.tracks[props.trackId].freeBeatLink || '',
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  onChange: (value: boolean, trackId: string) =>
    dispatch(
      new UpdateTrackField<'ownProduced'>(trackId, 'ownProduced', value)
    ),
  updateFile: (file: AssetFile, trackId: string) =>
    dispatch(
      new UpdateTrackField<'copyrightDocuments'>(
        trackId,
        'copyrightDocuments',
        [file]
      )
    ),
  onLinkAdded: (value: string, trackId: string) =>
    dispatch(
      new UpdateTrackField<'freeBeatLink'>(trackId, 'freeBeatLink', value)
    ),
});
const CoverCopyrightMaterialCtrl = (
  props: CoverCopyrightMaterialCtrlProps & StateProps
) => {
  const [selected, setSelected] = React.useState<TermsType>('');
  const [fileError, setFileError] = React.useState('');
  const { ownProduced, freeBeatLink } = props;

  React.useEffect(() => {
    setSelected(ownProduced);
  }, [ownProduced]);

  const handleSelected = (value: TermsType) => {
    setSelected(value);
    props.onChange(value === 'produced', props.trackId);
  };

  const Terms = [
    {
      value: 'produced' as TermsType,
      label: 'RELEASE.TRACK_INFO.TRACK.COPYRIGHTED_MATERIALS.PRODUCED.TEXT',
    },
    {
      value: 'purchased' as TermsType,
      label: 'RELEASE.TRACK_INFO.TRACK.COPYRIGHTED_MATERIALS.PURCHASED.TEXT',
    },
  ];

  return (
    <Box className={props.className} noBorderTop>
      <BoxHeader
        title={
          <Translate id="RELEASE.TRACK_INFO.TRACK.COPYRIGHTED_MATERIALS.TITLE" />
        }
        text={
          <Translate id="RELEASE.TRACK_INFO.TRACK.COPYRIGHTED_MATERIALS.TEXT" />
        }
      />
      <Wrapper>
        {Terms.map((term, i) => (
          <TermBox
            className={`term ${selected === term.value ? 'active' : ''} ${
              selected === 'purchased' ? 'no-border-bottom' : ''
            }`}
            onClick={() => handleSelected(term.value)}
            key={`${term.value}-${i}`}
          >
            <Typography weight="700">
              <Translate id={term.label} />
            </Typography>
            <AnimatePresence exitBeforeEnter>
              {selected === term.value ? (
                <MotionOpacity>
                  <CompletedIcon className="icon" />
                </MotionOpacity>
              ) : (
                <MotionOpacity>
                  <UnselectedIcon className="icon" />
                </MotionOpacity>
              )}
            </AnimatePresence>
          </TermBox>
        ))}
      </Wrapper>
      <ErrorMessage hasBackground errorId={fileError ? fileError : undefined} />
      <AnimatePresence exitBeforeEnter>
        {selected === 'purchased' ? (
          <MotionOpacity>
            <Flex flexDirection="column" gap={5}>
              <InputWrapper>
                <InputField
                  label={
                    <Translate id="RELEASE.TRACK_INFO.TRACK.COPYRIGHTED_MATERIALS.FREE_BEAT" />
                  }
                  type="text"
                  value={freeBeatLink}
                  onChange={(value) => props.onLinkAdded(value, props.trackId)}
                />
              </InputWrapper>
              <UploadFilesCtrl
                onChange={(file) => {
                  setFileError('');
                  if (file) props.updateFile(file, props.trackId);
                }}
                uploadedFilename={props.uploadedFilename}
                onError={setFileError}
              />
            </Flex>
          </MotionOpacity>
        ) : null}
      </AnimatePresence>
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(CoverCopyrightMaterialCtrl)``);

const Wrapper = styled.div`
  display: grid;
  grid-gap: 10px;
`;

const TermBox = styled.div`
  cursor: pointer;
  padding: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background-color: ${({ theme }) => theme.colors.differentBackground};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  span {
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};
    span {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }

  &.no-border-bottom {
    border-radius: 4px 4px 0 0;
  }

  .icon {
    width: 32px;
    height: 32px;
  }
`;

const InputWrapper = styled.div`
  border-radius: 4px 4px 0px 0px;
  border-width: 1px;
  border-style: solid;
  border-color: transparent transparent transparent rgb(255, 255, 255);
  border-image: none;
  position: relative;
  background-color: rgb(255, 255, 255);
  transition: border 0.275s ease-in-out 0s;
`;
